import { Ref, ref, computed, onMounted, watch } from 'vue';
import {
	ParsedTraceWorksheet,
	TraceWorksheetStats,
	TraceWorksheetFilters,
	AlgorithmExecution,
	ActivityType
} from '@/models/TraceWorksheetModels';
import { parseTraceWorksheet, computeStats } from './UseTraceWorksheetParser';

export function useTraceWorksheetAnalyzer(docUrl: Ref<string>) {
	const isLoading = ref<boolean>(false);
	const errorMessage = ref<string | null>(null);
	const parsedWorksheet = ref<ParsedTraceWorksheet | null>(null);

	const filters = ref<TraceWorksheetFilters>({
		algorithmName: null,
		state: null,
		lineOfBusiness: null,
		searchText: '',
		activityType: null,
		rateKeyName: null,
	});

	const itemsPerPage = ref<number>(25);
	const currentPage = ref<number>(1);

	const loadWorksheet = async () => {
		if (!docUrl.value) {
			errorMessage.value = 'No document URL provided.';
			return;
		}

		isLoading.value = true;
		errorMessage.value = null;

		try {
			const response = await fetch(docUrl.value);
			if (!response.ok) {
				throw new Error(`Failed to fetch trace worksheet: ${response.status} ${response.statusText}`);
			}
			const rawText = await response.text();
			parsedWorksheet.value = parseTraceWorksheet(rawText);
		} catch (err) {
			errorMessage.value = err instanceof Error ? err.message : 'Unknown error loading worksheet';
		} finally {
			isLoading.value = false;
		}
	};

	const stats = computed<TraceWorksheetStats | null>(() => {
		if (!parsedWorksheet.value) return null;
		return computeStats(parsedWorksheet.value);
	});

	const distinctAlgorithmNames = computed(() => stats.value?.distinctAlgorithmNames ?? []);
	const distinctStates = computed(() => stats.value?.distinctStates ?? []);
	const distinctLinesOfBusiness = computed(() => stats.value?.distinctLinesOfBusiness ?? []);
	const distinctInsuranceLines = computed(() => stats.value?.distinctInsuranceLines ?? []);
	const distinctSections = computed(() =>
		parsedWorksheet.value?.sections.map(s => s.name) ?? []
	);
	const distinctRateKeyNames = computed(() => {
		if (!parsedWorksheet.value) return [];
		const names = new Set<string>();
		for (const algo of parsedWorksheet.value.allAlgorithms) {
			for (const act of algo.activities) {
				if (act.rateKeyLookup) names.add(act.rateKeyLookup.keyName);
				if (act.rateKeyResult) names.add(act.rateKeyResult.keyName);
			}
		}
		return [...names].sort();
	});

	const filteredAlgorithms = computed<AlgorithmExecution[]>(() => {
		if (!parsedWorksheet.value) return [];
		let result = parsedWorksheet.value.allAlgorithms;

		const f = filters.value;

		if (f.algorithmName) {
			const lower = f.algorithmName.toLowerCase();
			result = result.filter(a =>
				a.shortName.toLowerCase().includes(lower) ||
				a.algorithmName.toLowerCase().includes(lower) ||
				a.identifier.toLowerCase().includes(lower)
			);
		}
		if (f.state) {
			result = result.filter(a => a.state === f.state);
		}
		if (f.lineOfBusiness) {
			result = result.filter(a => a.lob === f.lineOfBusiness || a.insuranceLine === f.lineOfBusiness);
		}
		if (f.rateKeyName) {
			const rk = f.rateKeyName;
			result = result.filter(a =>
				a.activities.some(act =>
					(act.rateKeyLookup && act.rateKeyLookup.keyName === rk) ||
					(act.rateKeyResult && act.rateKeyResult.keyName === rk)
				)
			);
		}
		if (f.activityType) {
			result = result.filter(a =>
				a.activities.some(act => act.type === f.activityType)
			);
		}
		if (f.searchText) {
			const lower = f.searchText.toLowerCase();
			result = result.filter(a =>
				a.activities.some(act => act.rawText.toLowerCase().includes(lower))
			);
		}

		return result;
	});

	const totalPages = computed(() =>
		Math.ceil(filteredAlgorithms.value.length / itemsPerPage.value)
	);

	const paginatedAlgorithms = computed(() => {
		const start = (currentPage.value - 1) * itemsPerPage.value;
		return filteredAlgorithms.value.slice(start, start + itemsPerPage.value);
	});

	// Reset page when filters change
	watch(filters, () => { currentPage.value = 1; }, { deep: true });

	const clearFilters = () => {
		filters.value = {
			algorithmName: null,
			state: null,
			lineOfBusiness: null,
			searchText: '',
			activityType: null,
			rateKeyName: null,
		};
	};

	onMounted(loadWorksheet);

	return {
		isLoading,
		errorMessage,
		parsedWorksheet,
		filters,
		stats,
		filteredAlgorithms,
		paginatedAlgorithms,
		distinctAlgorithmNames,
		distinctStates,
		distinctLinesOfBusiness,
		distinctInsuranceLines,
		distinctSections,
		distinctRateKeyNames,
		itemsPerPage,
		currentPage,
		totalPages,
		clearFilters,
		loadWorksheet,
	};
}
